import React from "react";
import {
  Paper,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Stack,
  Tooltip
} from "@mui/material";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import EventIcon from "@mui/icons-material/Event";
import UpdateIcon from "@mui/icons-material/Update";
import SyncIcon from "@mui/icons-material/Sync";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import GavelIcon from "@mui/icons-material/Gavel";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { useSiteVarsFunctions } from "../hooks/use-site-vars-functions";

export const LeaderboardStatusPanel = () => {
  const {
    formatGameWeek,
    getCurrentGameweekEvent,
    getNextGameweekEvent,
    getScoresUpdatedTime,
    getCurrentWindow,
    getPhase,
    getBiddingOpen,
    getOffersOpen
  } = useSiteVarsFunctions();

  const currentEvent = getCurrentGameweekEvent();
  const nextEvent = getNextGameweekEvent();
  const scoresUpdatedTime = getScoresUpdatedTime();
  const currentWindow = getCurrentWindow();
  const currentPhase = getPhase();
  const biddingOpen = getBiddingOpen();
  const offersOpen = getOffersOpen();

  // Format date helper
  const formatDate = (isoString) => {
    if (!isoString) return "TBD";
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return isoString;
    }
  };

  // Relative time helper
  const getRelativeTime = (isoString) => {
    if (!isoString) return "";
    try {
      const target = new Date(isoString).getTime();
      const now = Date.now();
      const diffMs = target - now;
      if (diffMs <= 0) return "(Deadline passed)";
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const days = Math.floor(diffHours / 24);
      const hours = diffHours % 24;
      if (days > 0) return `(in ${days}d ${hours}h)`;
      return `(in ${hours}h)`;
    } catch {
      return "";
    }
  };

  // Format last updated timestamp
  const formatLastUpdated = (timeVal) => {
    if (!timeVal) return "Never";
    try {
      let dateObj;
      if (timeVal._seconds !== undefined) {
        dateObj = new Date(timeVal._seconds * 1000 + (timeVal._nanoseconds || 0) / 1000000);
      } else {
        dateObj = new Date(timeVal);
      }
      return dateObj.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return "Unknown";
    }
  };

  // Determine current gameweek status
  let statusLabel = "Upcoming Kickoff";
  let statusColor = "info"; // default blue
  if (currentEvent) {
    if (currentEvent.finished) {
      statusLabel = "Matches Completed (Awaiting Next Kickoff Rollover)";
      statusColor = "warning"; // amber/yellow
    } else if (currentEvent.is_current) {
      statusLabel = "Live Matches in Progress";
      statusColor = "success"; // green
    } else {
      statusLabel = "Upcoming Kickoff";
      statusColor = "primary";
    }
  }

  return (
    <Paper
      elevation={2}
      sx={{
        mt: 3,
        mb: 2,
        p: 2,
        borderRadius: 2,
        backgroundColor: "background.paper",
        border: "1px solid rgba(0, 0, 0, 0.08)"
      }}
    >
      {/* Header Row */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mb: 1.5
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SportsSoccerIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight="bold">
            League Status & Gameweek Schedule
          </Typography>
        </Box>
        <Tooltip title="Current active competition status">
          <Chip
            size="small"
            label={statusLabel}
            color={statusColor}
            variant="filled"
            sx={{ fontWeight: "medium" }}
          />
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Main Metadata Grid */}
      <Grid container spacing={2}>
        {/* Column 1: Current Gameweek */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Stack spacing={0.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <EventIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                CURRENT GAMEWEEK
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="bold">
              {formatGameWeek()} {currentEvent?.name ? `(${currentEvent.name})` : ""}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Kickoff / Start: <b>{formatDate(currentEvent?.deadline_time)}</b>
            </Typography>
          </Stack>
        </Grid>

        {/* Column 2: Next Gameweek */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Stack spacing={0.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <UpdateIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                NEXT GAMEWEEK
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="bold">
              {nextEvent ? nextEvent.name : "End of Season"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Deadline: <b>{formatDate(nextEvent?.deadline_time)}</b>{" "}
              <span style={{ color: "#1976d2", fontWeight: 500 }}>
                {getRelativeTime(nextEvent?.deadline_time)}
              </span>
            </Typography>
          </Stack>
        </Grid>

        {/* Column 3: Score Sync & Cloud Health */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Stack spacing={0.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <SyncIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                LIVE SCORE SYNC
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="bold">
              {formatLastUpdated(scoresUpdatedTime)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Sync Schedule: <b>Every 15 mins (7am – 11pm UK)</b>
            </Typography>
          </Stack>
        </Grid>

        {/* Column 4: Transfer Market & Deals */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Stack spacing={0.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <GavelIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                MARKET & TRANSFERS
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="bold">
              Window {currentWindow} (Phase {currentPhase})
            </Typography>
            <Box sx={{ display: "flex", gap: 0.75, mt: 0.25 }}>
              <Chip
                size="small"
                label={biddingOpen ? "Bids Open" : "Bids Closed"}
                color={biddingOpen ? "success" : "default"}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
              <Chip
                size="small"
                label={offersOpen ? "Offers Open" : "Offers Closed"}
                color={offersOpen ? "success" : "default"}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.7rem" }}
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {/* Bonus Benchmarks (if available from FPL live data) */}
      {currentEvent && (currentEvent.average_entry_score > 0 || currentEvent.highest_score > 0) && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <QueryStatsIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                FPL GLOBAL BENCHMARK ({currentEvent.name}):
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Global Average: <b>{currentEvent.average_entry_score} pts</b>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Highest Score: <b>{currentEvent.highest_score} pts</b>
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};
